import type { UserRole } from '@/api/types';
import type { BreadcrumbItem, NavItem, NavSection } from '@/lib/types';
import { routes } from '@/lib/routePaths';

const overview: NavItem = { label: 'Overview', path: routes.overview, breadcrumb: 'Overview', icon: 'LayoutDashboard' };
const datasets: NavItem = { label: 'Datasets', path: routes.datasets, breadcrumb: 'Datasets', icon: 'Database' };
const strategies: NavItem = { label: 'Strategies', path: routes.strategies, breadcrumb: 'Strategies', icon: 'Code' };
const backtests: NavItem = { label: 'Backtests', path: routes.backtests, breadcrumb: 'Backtests', icon: 'Play' };
const trading: NavItem = { label: 'Trading operations', path: routes.trading, breadcrumb: 'Trading operations', icon: 'Activity' };
const manual: NavItem = { label: 'Manual trading', path: '/trading/manual', breadcrumb: 'Manual trading', icon: 'Activity' };
const connections: NavItem = { label: 'Broker connections', path: routes.tradingConnections, breadcrumb: 'Broker connections', icon: 'Cable' };
const users: NavItem = { label: 'Users', path: routes.users, breadcrumb: 'Users', icon: 'Users' };
const alerting: NavItem = { label: 'Alerting system', path: routes.alertingBot, breadcrumb: 'Alerting system', icon: 'Bell' };
const logs: NavItem = { label: 'System logs', path: routes.logs, breadcrumb: 'System logs', icon: 'ScrollText' };

const allNavItems = [overview, datasets, strategies, backtests, trading, manual, connections, users, alerting, logs];

export function getNavSections(role?: UserRole): NavSection[] {
  const sections: NavSection[] = [
    { items: [overview] },
    { label: 'Build & Test', items: [datasets, strategies, backtests] },
  ];
  if (role === 'Trader' || role === 'Admin') sections.push({ label: 'Live Trading', items: [trading, manual, connections] });
  if (role === 'Admin') sections.push({ label: 'Administration', items: [users, alerting, logs] });
  return sections;
}

export function resolveActiveNavPath(pathname: string) {
  if (pathname === '/') return '/';
  if (pathname.startsWith('/admin/alerting')) return routes.alertingBot;
  return allNavItems
    .filter((item) => item.path !== '/')
    .sort((left, right) => right.path.length - left.path.length)
    .find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))?.path ?? pathname;
}

export function isNavItemActive(pathname: string, itemPath: string) {
  return resolveActiveNavPath(pathname) === itemPath;
}

export function isStrategyBuilderPath(pathname: string) {
  return pathname === routes.newStrategy || /^\/strategies\/[^/]+\/edit$/.test(pathname);
}

export function strategyBuilderPath(strategyId?: string | null) {
  return strategyId ? routes.strategyEditor(strategyId) : routes.newStrategy;
}

export function getBreadcrumbs(pathname: string, entityLabel?: string): BreadcrumbItem[] {
  if (/^\/trading\/deployments\/[^/]+$/.test(pathname)) {
    return [{ label: 'Trading operations', path: routes.trading }, { label: entityLabel || 'Deployment' }];
  }
  if (pathname === routes.tradingConnections) {
    return [{ label: 'Trading operations', path: routes.trading }, { label: 'Broker connections' }];
  }
  if (pathname.startsWith('/datasets/')) {
    return [{ label: 'Datasets', path: routes.datasets }, { label: entityLabel || 'Dataset' }];
  }
  if (pathname.startsWith('/backtests/')) {
    return [{ label: 'Backtests', path: routes.backtests }, { label: entityLabel || 'Backtest run' }];
  }
  if (pathname === routes.newStrategy) {
    return [{ label: 'Strategies', path: routes.strategies }, { label: 'New strategy' }];
  }
  if (/^\/strategies\/[^/]+\/edit$/.test(pathname)) {
    return [{ label: 'Strategies', path: routes.strategies }, { label: entityLabel || 'Strategy editor' }];
  }
  if (pathname.startsWith('/settings/')) {
    return [{ label: pathname === routes.security ? 'Security' : 'My alerts' }];
  }
  if (pathname.startsWith('/admin/alerting/')) {
    const child = pathname === routes.alertingPolicy ? 'Global policy' : pathname === routes.alertingDelivery ? 'Delivery health' : 'Bot setup';
    return [{ label: 'Alerting system', path: routes.alertingBot }, { label: child }];
  }
  if (pathname === routes.forbidden) return [{ label: 'Access denied' }];

  const activeItem = allNavItems.find((item) => item.path === resolveActiveNavPath(pathname));
  return [{ label: activeItem?.breadcrumb ?? activeItem?.label ?? 'Not found' }];
}
